import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { deviceFingerprintService } from '../services/deviceFingerprint';

const API_BASE_URL = process.env.REACT_APP_API_URL;
// console.log("Backend URL:", API_BASE_URL); // Temporary debug log

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      provider: null,
      // Device recognition state
      isRecognizedDevice: false,
      isCheckingDevice: false,
      deviceInfo: null,
      shouldSkipOtp: false, // For OAuth providers like Google or Outlook
      setUser: (user) => set({ user }),
      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),
      setProvider: (provider) => set({ provider }),
      login: async (email, password, fingerprint) => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, fingerprint }), // <-- Add fingerprint
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.message || 'Login failed');
          }

          // Check if OTP is required
          if (result.data.requiresOtp) {
            return {
              ...result,
              requiresOtp: true,
              email: result.data.email,
            };
          }

          // If no OTP required (fallback for backward compatibility)
          const user = result.data.user;
          const accessToken = result.data.tokens.accessToken;
          const refreshToken = result.data.tokens.refreshToken;

          // Store device fingerprint for future recognition
          if (fingerprint && user?.id) {
            get().storeDeviceFingerprint(user.id, fingerprint);
          }

          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
          });

          return result;
        } catch (error) {
          throw error;
        }
      },
      requestOtp: async (email, password) => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/request-otp`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.message || 'OTP request failed');
          }

          return result;
        } catch (error) {
          throw error;
        }
      },
      verifyOtp: async (email, otp, fingerprint) => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, otp, fingerprint }),
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.message || 'OTP verification failed');
          }

          const user = result.data.user;
          const accessToken = result.data.tokens.accessToken;
          const refreshToken = result.data.tokens.refreshToken;

          // Store device fingerprint for future recognition
          if (fingerprint && user?.id) {
            get().storeDeviceFingerprint(user.id, fingerprint);
          }

          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
          });

          return result;
        } catch (error) {
          throw error;
        }
      },
      refreshAccessToken: async () => {
        try {
          const { refreshToken } = get();

          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Token refresh failed');
          }

          set({
            accessToken:
              data.data.tokens.access_token || data.data.tokens.accessToken,
            refreshToken:
              data.data.tokens.refresh_token || data.data.tokens.refreshToken,
          });

          return {
            accessToken:
              data.data.tokens.access_token || data.data.tokens.accessToken,
            refreshToken:
              data.data.tokens.refresh_token || data.data.tokens.refreshToken,
          };
        } catch (error) {
          // Don't logout here — the api interceptor handles retries and
          // scheduled logout.  Calling logout() prematurely wipes user
          // state (and features) even on transient failures.
          throw error;
        }
      },
      logout: () => {
        // Clear device fingerprint on logout
        deviceFingerprintService.clearStoredDeviceFingerprint();
        // Clear cached features for this org
        try {
          const orgId = get().user?.organizationId;
          if (orgId) localStorage.removeItem('govify_features_' + orgId);
        } catch (_) { /* feature cache cleanup is best-effort */ }

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },
      // Check if current device is recognized for a user
      isDeviceRecognized: async (userId) => {
        return await deviceFingerprintService.isDeviceRecognized(userId);
      },
      // Get stored device info
      getDeviceInfo: () => {
        return deviceFingerprintService.getDeviceInfo();
      },
      // Check device recognition and update state
      checkDeviceRecognition: async () => {
        const { user } = get();
        if (!user?.id) {
          set({
            isRecognizedDevice: false,
            deviceInfo: null,
            shouldSkipOtp: false,
          });
          return false;
        }

        set({ isCheckingDevice: true });

        try {
          const isRecognized = await deviceFingerprintService.isRecognized(
            user.id,
          );
          const deviceInfo = isRecognized
            ? deviceFingerprintService.getDeviceInfo()
            : null;

          // Check if this is a new tab scenario
          const isNewTab = !sessionStorage.getItem('tabInitialized');
          if (isNewTab) {
            sessionStorage.setItem('tabInitialized', 'true');
          }

          const shouldSkipOtp = isRecognized && (isNewTab || true); // Allow skip for both new and same tab

          set({
            isRecognizedDevice: isRecognized,
            deviceInfo,
            shouldSkipOtp,
            isCheckingDevice: false,
          });

          // Console logging
          if (isNewTab && isRecognized) {
            console.log(
              '🔐 New tab detected with recognized device - OTP can be skipped',
            );
          } else if (isNewTab && !isRecognized) {
            console.log(
              '🔒 New tab detected with unrecognized device - OTP required',
            );
          } else if (!isNewTab && isRecognized) {
            console.log(
              '✅ Same tab with recognized device - OTP can be skipped',
            );
          } else {
            console.log('❌ Device not recognized - OTP required');
          }

          return isRecognized;
        } catch (error) {
          console.error('Error checking device recognition:', error);
          set({
            isRecognizedDevice: false,
            deviceInfo: null,
            shouldSkipOtp: false,
            isCheckingDevice: false,
          });
          return false;
        }
      },
      // Store device fingerprint and update state
      storeDeviceFingerprint: (userId, fingerprint) => {
        deviceFingerprintService.storeDeviceFingerprint(userId, fingerprint);
        set({
          isRecognizedDevice: true,
          deviceInfo: deviceFingerprintService.getDeviceInfo(),
        });
      },
      forgotPassword: async (email) => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Failed to send reset email');
          }

          return data;
        } catch (error) {
          throw error;
        }
      },
      resetPassword: async (token, newPassword) => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token, newPassword }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Failed to reset password');
          }

          return data;
        } catch (error) {
          throw error;
        }
      },
      getUserSessions: async () => {
        try {
          const { accessToken } = get();
          const response = await fetch(`${API_BASE_URL}/auth/sessions`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch sessions');
          }

          return data;
        } catch (error) {
          throw error;
        }
      },
      revokeAllSessions: async () => {
        try {
          const { accessToken } = get();
          const response = await fetch(
            `${API_BASE_URL}/auth/sessions/revoke-all`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
            },
          );

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Failed to revoke sessions');
          }

          return data;
        } catch (error) {
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

// Custom hook for auth-related utilities
export const useAuth = () => {
  const {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    setUser,
    setTokens,
    login,
    logout,
    refreshAccessToken,
    forgotPassword,
    resetPassword,
    getUserSessions,
    revokeAllSessions,
    isDeviceRecognized,
    getDeviceInfo,
    checkDeviceRecognition,
    storeDeviceFingerprint,
    isRecognizedDevice,
    isCheckingDevice,
    deviceInfo,
    shouldSkipOtp,
  } = useAuthStore();

  return {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    setUser,
    setTokens,
    login,
    logout,
    refreshAccessToken,
    forgotPassword,
    resetPassword,
    getUserSessions,
    revokeAllSessions,
    isDeviceRecognized,
    getDeviceInfo,
    checkDeviceRecognition,
    storeDeviceFingerprint,
    isRecognizedDevice,
    isCheckingDevice,
    deviceInfo,
    shouldSkipOtp,
  };
};
